import { FC, useEffect, useMemo, useState, useRef } from 'react'
import styled, { css } from 'styled-components'
import { margin, MarginProps } from 'styled-system'
import { Z_INDEX } from 'utils/constants'

import Icon from 'components/Icon'
import Text from 'components/Text'
import Tooltip from 'components/Tooltip'

import { Document } from 'types/global'
import { HexToRgba } from 'utils/colors'
import { getDocType, isImage } from 'utils/file'
import { PopOverItem } from 'components/PopOver/types'
import MoreOptionsPopOver from 'components/MoreOptionsPopOver'

type Props = Omit<Document, 'uploader' | 'documentUrl'> &
  MarginProps & {
    isLoading?: boolean
    onClick?: () => void
    onToggleMenu?: (isOpen: boolean) => void
    onDeleteDocument?: (documentId: string) => void
    onDownloadDocument?: (documentId: string) => void
    tooltipPosition?: 'top' | 'bottom' | 'left' | 'right' | 'bottomRight' | 'topRight' | 'topLeft' | 'bottomLeft'
    hideDeleteOption?: boolean
  }

enum Actions {
  delete = 'delete',
  download = 'download',
}

const DocumentTile: FC<Props> = ({
  documentId,
  fileName,
  mimeType,
  thumbnailUrl,
  tooltipPosition = 'bottom',
  hideDeleteOption = false,
  onClick,
  onToggleMenu,
  onDeleteDocument,
  onDownloadDocument,
  ...props
}) => {
  const [isHovered, setIsHovered] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [showImageFallback, setShowImageFallback] = useState(false)

  const parentRef = useRef<HTMLDivElement>(null)

  const { fileExtension, isDocuImage } = useMemo(
    () => ({
      fileExtension: getDocType(mimeType),
      isDocuImage: isImage(mimeType),
    }),
    [mimeType],
  )

  const handleMenuItemClick = (value?: PopOverItem) => {
    switch (value?.value) {
      case Actions.delete:
        onDeleteDocument?.(documentId)
        break
      case Actions.download:
        onDownloadDocument?.(documentId)
        break
      default:
    }
  }

  const handleMenuToggle = (isOpen: boolean) => {
    setIsMenuOpen(isOpen)
    onToggleMenu?.(isOpen)
  }

  const menuOptions: PopOverItem[] = useMemo(
    () => [
      {
        icon: ['fa', 'arrow-to-bottom'],
        label: 'download',
        value: Actions.download,
      },
      {
        icon: ['far', 'trash-alt'],
        iconColor: 'red',
        label: 'delete_lbl',
        labelColor: 'red',
        value: Actions.delete,
        isHidden: hideDeleteOption,
      },
    ],
    [hideDeleteOption],
  )

  useEffect(() => {
    let abort = false

    if (!abort) {
      setShowImageFallback(!Boolean(thumbnailUrl))
    }

    return () => {
      abort = true
    }
  }, [thumbnailUrl])

  useEffect(() => {
    const parent = parentRef?.current

    const handleMouseEnter = () => setIsHovered(true)
    const handleMouseLeave = () => setIsHovered(false)

    parent?.addEventListener('mouseenter', handleMouseEnter)
    parent?.addEventListener('mouseleave', handleMouseLeave)

    return () => {
      parent?.removeEventListener('mouseenter', handleMouseEnter)
      parent?.removeEventListener('mouseleave', handleMouseLeave)
    }
  })

  return (
    <Container
      {...props}
      onClick={() => isHovered && onClick?.()}
      showBorder={!isDocuImage || (isDocuImage && (showImageFallback || !thumbnailUrl))}
      ref={parentRef}
    >
      {(isHovered || isMenuOpen) && (
        <MenuContainer>
          <MoreOptionsPopOver
            options={menuOptions}
            onSelect={handleMenuItemClick}
            onToggleMenu={handleMenuToggle}
            isPortaled={false}
          />
        </MenuContainer>
      )}
      <Tooltip position={tooltipPosition} title={fileName}>
        {isDocuImage && !showImageFallback && (
          <>
            <ImageContainer alt={fileName} onError={() => setShowImageFallback(true)} src={thumbnailUrl} />
          </>
        )}
        {(!isDocuImage || (isDocuImage && (showImageFallback || !thumbnailUrl))) && (
          <DocumentContainer isHovered={isHovered || isMenuOpen}>
            <Icon
              fontSize={32}
              icon={isDocuImage && (showImageFallback || !thumbnailUrl) ? ['fal', 'file-image'] : ['fal', 'file-alt']}
            />
            <Text
              fontSize={13}
              textAlign="center"
              lineHeight="16px"
              mt={10}
              whiteSpace="normal"
              maxLines={2}
              ellipsis
              wordBreak="break-word"
            >
              {fileName}
            </Text>
            {fileExtension && (
              <Text color="gray5" fontSize={13} fontWeight="medium" lineHeight="16px">
                {fileExtension.toUpperCase()}
              </Text>
            )}
          </DocumentContainer>
        )}
      </Tooltip>
    </Container>
  )
}

const Container = styled.div<MarginProps & { showBorder: boolean }>`
  ${margin}
  align-items: center;
  border: 1px solid ${(p) => (p.showBorder ? p.theme.colors.gray4 : 'transparent')};
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  position: relative;
  user-select: none;

  :hover {
    border-color: ${(p) => p.theme.colors.blue2};
  }
`

const DocumentContainer = styled.div<{ isHovered: boolean }>`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 184px;
  width: 197px;
  overflow: hidden;
  border: 1px solid transparent;

  ${({ isHovered }) =>
    isHovered &&
    css`
      background-color: ${(p) => HexToRgba(p.theme.colors.black, 0.4)};

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border-radius: 6px;
        z-index: 1;
      }
    `}
`

const ImageContainer = styled.img`
  border-radius: 6px;
  width: 100%;
  height: 100%;
  object-fit: contain;
  background-color: ${(p) => p.theme.colors.white};
`

const MenuContainer = styled.div`
  position: absolute;
  right: 10px;
  top: 10px;
  z-index: ${Z_INDEX.dropdown};

  .pop-over-content {
    right: -4px;
    top: 32px;
  }
`

export default DocumentTile
